import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { Credencial, EstadoIA, IaService, Proveedor, PruebaCredencial } from 'src/app/services/ia/ia.service';
import { ORIGEN, PROVEEDORES, errorDeIA, errorDeProveedor, fechaHora, ms } from 'src/app/services/ia/ia-formato';

/**
 * LAS CLAVES DE LOS PROVEEDORES — de plataforma, o las de UN comercio.
 *
 * Se usa en dos lugares con el mismo marcado (CONTRATO §8.2: «misma forma que
 * arriba, con `comercio`»):
 *   · la solapa Estado de `console/ia`, sin `comercio` → claves de plataforma;
 *   · la solapa «Asistente de IA» de la ficha, con `comercio` → su clave propia,
 *     que manda sobre la de plataforma para ese comercio y nada más.
 *
 * 🔴 LA CLAVE NUNCA SE MUESTRA NI SE REGISTRA. Viaja una vez, en el PUT, desde un
 * campo de contraseña; al volver la respuesta el campo se vacía. De ahí en
 * adelante lo único que existe de este lado son los cuatro últimos
 * (`••••1234`). Ni `console.log`, ni la clave en un mensaje de error.
 *
 * 📍 «Cambiar la clave» y «Quitar» se abren EN LA FILA, no en un modal
 * (nada de modales, salvo `APAGAR`). Quitar pide un segundo clic en la misma
 * fila: es una línea, no un formulario.
 */
@Component({
  selector: 'app-ia-credenciales',
  templateUrl: './ia-credenciales.component.html',
  styleUrls: ['./ia-credenciales.component.css']
})
export class IaCredencialesComponent implements OnInit, OnChanges {

  /** Vacío = claves de plataforma. Con código = las del comercio. */
  @Input() comercio: string | null = null;
  /** El estado general, para decir qué clave se está usando y de dónde sale. */
  @Input() estado: EstadoIA | null = null;
  @Input() puedeEditar = false;
  /** Avisa que algo cambió, para que la pantalla relea el estado. */
  @Output() cambio = new EventEmitter<void>();

  readonly PROVEEDORES = PROVEEDORES;
  readonly ORIGEN = ORIGEN;
  readonly fechaHora = fechaHora;
  readonly ms = ms;
  readonly errorDeProveedor = errorDeProveedor;

  cargando = true;
  error: string | null = null;
  credenciales: Credencial[] = [];

  /** El proveedor cuya fila tiene abierto el campo de la clave. */
  editando: Proveedor | null = null;
  clave = '';
  /** El proveedor cuya fila pide confirmar «Quitar». */
  confirmandoQuitar: Proveedor | null = null;

  ocupado: { [p: string]: 'guardar' | 'probar' | 'quitar' | null } = {};
  aviso: { [p: string]: { ok: boolean; texto: string; crudo?: string } | null } = {};

  constructor(private _ia: IaService) { }

  ngOnInit(): void { this.cargar(); }

  ngOnChanges(ch: SimpleChanges): void {
    if (ch['comercio'] && !ch['comercio'].firstChange) this.cargar();
  }

  get alcance(): string { return this.comercio ? this.comercio.toUpperCase() : 'plataforma'; }

  cargar = async (): Promise<void> => {
    this.cargando = true;
    this.error = null;
    try {
      const todas = await this._ia.getCredenciales();
      this.credenciales = Array.isArray(todas) ? todas : [];
    } catch (e: any) {
      this.credenciales = [];
      this.error = errorDeIA(e);
    } finally {
      this.cargando = false;
    }
  };

  /** La guardada en ESTE alcance (plataforma o el comercio). */
  guardada = (p: Proveedor): Credencial | null =>
    this.credenciales.find((c) => c.proveedor === p && String(c.alcance).toUpperCase() === this.alcance.toUpperCase()) || null;

  /** La de plataforma, para decirle a un comercio sin clave propia cuál usa. */
  dePlataforma = (p: Proveedor): Credencial | null =>
    this.credenciales.find((c) => c.proveedor === p && c.alcance === 'plataforma') || null;

  /** Lo que dice el motor de este proveedor (sólo tiene sentido en plataforma). */
  enUso = (p: Proveedor) => this.estado?.proveedores?.[p] || null;

  get sinClaveMaestra(): boolean { return !!this.estado && this.estado.claveMaestra === false; }

  abrir = (p: Proveedor): void => {
    this.editando = p;
    this.clave = '';
    this.confirmandoQuitar = null;
    this.aviso[p] = null;
  };

  cancelar = (): void => { this.editando = null; this.clave = ''; };

  guardar = async (p: Proveedor): Promise<void> => {
    const clave = (this.clave || '').trim();
    if (!clave || this.ocupado[p]) return;
    this.ocupado[p] = 'guardar';
    this.aviso[p] = null;
    try {
      const r = await this._ia.guardarCredencial(p, clave, this.comercio || undefined);
      this.aviso[p] = { ok: true, texto: `Guardada. Termina en ${r?.ultimos4 || '····'}.` };
      this.editando = null;
      await this.cargar();
      this.cambio.emit();
    } catch (e: any) {
      this.aviso[p] = { ok: false, texto: errorDeIA(e, true) };
    } finally {
      // 🔴 La clave no se queda en memoria ni si falló.
      this.clave = '';
      this.ocupado[p] = null;
    }
  };

  probar = async (p: Proveedor): Promise<void> => {
    if (this.ocupado[p]) return;
    this.ocupado[p] = 'probar';
    this.aviso[p] = null;
    try {
      const r: PruebaCredencial = await this._ia.probarCredencial(p, this.comercio || undefined);
      this.aviso[p] = r?.ok
        ? { ok: true, texto: `Contestó en ${ms(r.latenciaMs)}${r.modeloId ? ` con ${r.modeloId}` : ''}.` }
        : { ok: false, texto: `No contestó: ${errorDeProveedor(r?.error)}`, crudo: r?.error || undefined };
      this.cambio.emit();
    } catch (e: any) {
      this.aviso[p] = { ok: false, texto: errorDeIA(e, true) };
    } finally {
      this.ocupado[p] = null;
    }
  };

  pedirQuitar = (p: Proveedor): void => {
    this.confirmandoQuitar = p;
    this.editando = null;
    this.aviso[p] = null;
  };

  quitar = async (p: Proveedor): Promise<void> => {
    if (this.ocupado[p]) return;
    this.ocupado[p] = 'quitar';
    try {
      await this._ia.quitarCredencial(p, this.comercio || undefined);
      this.aviso[p] = { ok: true, texto: 'Se quitó la clave.' };
      this.confirmandoQuitar = null;
      await this.cargar();
      this.cambio.emit();
    } catch (e: any) {
      this.aviso[p] = { ok: false, texto: errorDeIA(e, true) };
    } finally {
      this.ocupado[p] = null;
    }
  };
}
